import { useEffect, useState } from 'react'
import { AsyncButton } from '@shared/ui/AsyncButton'
import { EmptyState } from '@shared/ui/EmptyState'
import { Notice } from '@shared/ui/Notice'
import { PasswordInput } from '@shared/ui/PasswordInput'
import { useEmergencyAccess } from '@features/emergency-access/hooks/useEmergencyAccess'
import {
  EmergencyError,
  emergencyTakeoverFlow,
} from '@features/emergency-access/services/emergencyAccessFlow'
import type {
  EmergencyAccessStatus,
  GrantListItemWire,
  ReceivedListItemWire,
} from '@features/emergency-access/types'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/** マスターパスワード最小長 (登録時ポリシーと同じ)。 */
const MASTER_PASSWORD_MIN_LEN = 12

const TAKEOVER_ERROR_KEYS: Record<EmergencyError['code'], string> = {
  no_session: 'settings.emergency.error.no_session',
  already_initialized: 'settings.emergency.error.generic',
  self_invite: 'settings.emergency.error.generic',
  duplicate_invite: 'settings.emergency.error.generic',
  not_found: 'settings.emergency.error.not_found',
  bad_request: 'settings.emergency.error.bad_request',
  forbidden: 'settings.emergency.error.forbidden',
  too_many_requests: 'settings.emergency.error.too_many_requests',
  network: 'settings.emergency.error.network',
  generic: 'settings.emergency.error.generic',
}

/**
 * Settings § emergency: 緊急アクセス管理 (Track F、Phase 5)。
 *
 * 2 paneles:
 *   - Granter: 自分が信頼する人を invite + 状態管理 (REQUESTED → approve/deny、revoke)
 *   - Grantee: 自分が受領した invite を accept/decline/request/cancel/unlock
 *
 * 注意:
 *   - invite フォームの初回呼出時に grantee 自身の `emergencyPubKey` が必要なため、
 *     mount 時に lazy `initKeypair()` を呼ぶ (idempotent: 既に init 済なら何もしない)
 *   - unlock した vault は `#emergency/vault/:id` (F-C9 で実装) に遷移する
 */

/**
 * 待機日数の選択肢。-1 = 無期限 (自動承認しない、granter の明示承認のみ)。
 * 無期限は通常値ではないため末尾に置く。default は 7 日。
 */
const WAIT_OPTIONS = [3, 7, 14, 30, -1] as const

/** invite フォームの待機日数の初期値。 */
const DEFAULT_WAIT_DAYS = 7

/** 待機日数の表示文字列。-1 = 無期限。 */
const formatWait = (t: (k: string) => string, waitDays: number): string =>
  waitDays < 0
    ? t('settings.emergency.wait_permanent')
    : t('settings.emergency.wait_days_label').replace('{days}', String(waitDays))

const TERMINAL_STATUSES: readonly EmergencyAccessStatus[] = ['REVOKED', 'DECLINED']

const isTerminal = (status: EmergencyAccessStatus): boolean => TERMINAL_STATUSES.includes(status)

const statusBadgeClass = (status: EmergencyAccessStatus): string => {
  switch (status) {
    case 'REQUESTED':
      return 'tag warn'
    case 'APPROVED':
      return 'tag danger'
    case 'ACCEPTED':
    case 'INVITED':
      return 'tag'
    case 'DECLINED':
    case 'REVOKED':
      return 'tag muted'
    default:
      return 'tag'
  }
}

const GrantRow = ({
  grant,
  onApprove,
  onDeny,
  onRevoke,
  onEdit,
  onRewrap,
  onDelete,
}: {
  grant: GrantListItemWire
  onApprove: (id: string) => Promise<void>
  onDeny: (id: string) => Promise<void>
  /** 取消ボタン押下 — 確認ダイアログを開く (即時 revoke しない)。 */
  onRevoke: (grant: GrantListItemWire) => void
  /** 編集ボタン押下 — 編集ダイアログを開く (INVITED / ACCEPTED のみ表示)。 */
  onEdit: (grant: GrantListItemWire) => void
  /** 再ラップ — placeholder で wrappedSk=NULL のまま残った grant を再 wrap する (L-24)。 */
  onRewrap: (grant: GrantListItemWire) => Promise<void>
  /** 履歴行 (terminal) の削除ボタン。指定時のみ表示。 */
  onDelete?: (id: string) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const isActive = grant.status !== 'REVOKED' && grant.status !== 'DECLINED'
  // 待機日数 / アクセス種別の編集は解錠フロー開始前 (INVITED / ACCEPTED) のみ許可。
  const isEditable = grant.status === 'INVITED' || grant.status === 'ACCEPTED'

  return (
    <div className="card settings-emergency-row" data-testid={`grant-row-${grant.id}`}>
      <div>
        <div className="settings-item-label">{grant.granteeEmail}</div>
        <div className="settings-item-sub">
          <span className={statusBadgeClass(grant.status)}>
            {t(`settings.emergency.status.${grant.status}`)}
          </span>
          {' · '}
          <span className={`tag${grant.accessType === 'takeover' ? ' warn' : ''}`}>
            {t(
              grant.accessType === 'takeover'
                ? 'settings.emergency.access_type.takeover'
                : 'settings.emergency.access_type.view',
            )}
          </span>
          {' · '}
          {formatWait(t, grant.waitDays)}
          {!grant.granteeRegistered && (
            <>
              {' · '}
              <span className="tag muted">{t('settings.emergency.placeholder')}</span>
            </>
          )}
          {grant.requiresWrap && (
            <>
              {' · '}
              <span className="tag warn">{t('settings.emergency.requires_wrap')}</span>
            </>
          )}
        </div>
      </div>
      <div className="settings-emergency-actions">
        {grant.requiresWrap && grant.granteePubKey !== null && (
          <AsyncButton
            className="btn btn-secondary btn-sm"
            onClick={() => onRewrap(grant)}
            data-testid={`grant-rewrap-${grant.id}`}
          >
            <Icon name="refresh" aria-hidden="true" />
            {t('settings.emergency.action.rewrap')}
          </AsyncButton>
        )}
        {grant.status === 'REQUESTED' && (
          <>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => void onApprove(grant.id)}
              data-testid={`grant-approve-${grant.id}`}
            >
              {t('settings.emergency.action.approve')}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => void onDeny(grant.id)}
              data-testid={`grant-deny-${grant.id}`}
            >
              {t('settings.emergency.action.deny')}
            </button>
          </>
        )}
        {isEditable && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => onEdit(grant)}
            data-testid={`grant-edit-${grant.id}`}
          >
            {t('settings.emergency.action.edit')}
          </button>
        )}
        {isActive && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => onRevoke(grant)}
            data-testid={`grant-revoke-${grant.id}`}
          >
            {t('settings.emergency.action.revoke')}
          </button>
        )}
        {!isActive && onDelete && (
          <button
            type="button"
            className="icon-btn sm"
            onClick={() => onDelete(grant.id)}
            aria-label={t('settings.emergency.action.delete_history')}
            title={t('settings.emergency.action.delete_history')}
            data-testid={`grant-delete-${grant.id}`}
          >
            <Icon name="trash" />
          </button>
        )}
      </div>
    </div>
  )
}

const ReceivedRow = ({
  received,
  onAccept,
  onDecline,
  onRequest,
  onCancelRequest,
  onUnlock,
  onTakeover,
  onDelete,
}: {
  received: ReceivedListItemWire
  onAccept: (id: string) => Promise<void>
  onDecline: (id: string) => Promise<void>
  onRequest: (id: string) => Promise<void>
  onCancelRequest: (id: string) => Promise<void>
  onUnlock: (id: string) => void
  /** accessType='takeover' の APPROVED invite でテイクオーバーを開始する。 */
  onTakeover: (received: ReceivedListItemWire) => void
  /** 履歴行 (terminal) の削除ボタン。指定時のみ表示。 */
  onDelete?: (id: string) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const isHistory = received.status === 'REVOKED' || received.status === 'DECLINED'
  const isTakeover = received.accessType === 'takeover'

  return (
    <div className="card settings-emergency-row" data-testid={`received-row-${received.id}`}>
      <div>
        <div className="settings-item-label">{received.granterEmail}</div>
        <div className="settings-item-sub">
          <span className={statusBadgeClass(received.status)}>
            {t(`settings.emergency.status.${received.status}`)}
          </span>
          {' · '}
          <span className={`tag${isTakeover ? ' warn' : ''}`}>
            {t(
              isTakeover
                ? 'settings.emergency.access_type.takeover'
                : 'settings.emergency.access_type.view',
            )}
          </span>
          {' · '}
          {formatWait(t, received.waitDays)}
        </div>
      </div>
      <div className="settings-emergency-actions">
        {received.status === 'INVITED' && (
          <>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => void onAccept(received.id)}
              data-testid={`received-accept-${received.id}`}
            >
              {t('settings.emergency.action.accept')}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => void onDecline(received.id)}
              data-testid={`received-decline-${received.id}`}
            >
              {t('settings.emergency.action.decline')}
            </button>
          </>
        )}
        {received.status === 'ACCEPTED' && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => void onRequest(received.id)}
            data-testid={`received-request-${received.id}`}
          >
            {t('settings.emergency.action.request')}
          </button>
        )}
        {received.status === 'REQUESTED' && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => void onCancelRequest(received.id)}
            data-testid={`received-cancel-${received.id}`}
          >
            {t('settings.emergency.action.cancel_request')}
          </button>
        )}
        {received.status === 'APPROVED' && isTakeover && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => onTakeover(received)}
            data-testid={`received-takeover-${received.id}`}
          >
            {t('settings.emergency.action.takeover')}
          </button>
        )}
        {received.status === 'APPROVED' && !isTakeover && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => onUnlock(received.id)}
            data-testid={`received-unlock-${received.id}`}
          >
            {t('settings.emergency.action.unlock')}
          </button>
        )}
        {isHistory && onDelete && (
          <button
            type="button"
            className="icon-btn sm"
            onClick={() => onDelete(received.id)}
            aria-label={t('settings.emergency.action.delete_history')}
            title={t('settings.emergency.action.delete_history')}
            data-testid={`received-delete-${received.id}`}
          >
            <Icon name="trash" />
          </button>
        )}
      </div>
    </div>
  )
}

export const EmergencySection = (): JSX.Element => {
  const { t } = useTranslation()

  const {
    state,
    lastErrorKey,
    invite,
    accept,
    decline,
    request,
    cancelRequest,
    approve,
    deny,
    revoke,
    editGrant,
    deleteEntry,
    rewrap,
    initKeypair,
    refresh,
  } = useEmergencyAccess()

  /** placeholder の grant を再 wrap する (L-24)。granteePubKey が無ければ何もしない。 */
  const onRewrapGrant = async (grant: GrantListItemWire): Promise<void> => {
    if (grant.granteePubKey === null) return
    await rewrap(grant.id, grant.granteePubKey)
  }

  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteWaitDays, setInviteWaitDays] = useState(DEFAULT_WAIT_DAYS)
  const [inviteAccessType, setInviteAccessType] = useState<'view' | 'takeover'>('view')
  const [submitting, setSubmitting] = useState(false)
  const [showGrantHistory, setShowGrantHistory] = useState(false)
  const [showReceivedHistory, setShowReceivedHistory] = useState(false)
  // #4: 取消の確認ダイアログ対象 (null = 非表示)。
  const [revokeTarget, setRevokeTarget] = useState<GrantListItemWire | null>(null)
  const [revoking, setRevoking] = useState(false)
  // #3: テイクオーバー モーダル。
  const [takeoverTarget, setTakeoverTarget] = useState<ReceivedListItemWire | null>(null)
  const [takeoverPw, setTakeoverPw] = useState('')
  const [takeoverPw2, setTakeoverPw2] = useState('')
  const [takeoverPhase, setTakeoverPhase] = useState<'idle' | 'submitting' | 'done'>('idle')
  const [takeoverErrorKey, setTakeoverErrorKey] = useState<string | null>(null)
  const [takeoverDoneEmail, setTakeoverDoneEmail] = useState('')
  // 信頼者ルールの編集モーダル (待機日数 / アクセス種別)。
  const [editTarget, setEditTarget] = useState<GrantListItemWire | null>(null)
  const [editWaitDays, setEditWaitDays] = useState(7)
  const [editAccessType, setEditAccessType] = useState<'view' | 'takeover'>('view')
  const [editing, setEditing] = useState(false)

  const onConfirmRevoke = async (): Promise<void> => {
    if (revokeTarget === null || revoking) return
    setRevoking(true)
    try {
      await revoke(revokeTarget.id)
      setRevokeTarget(null)
    } finally {
      setRevoking(false)
    }
  }

  const openEditGrant = (grant: GrantListItemWire): void => {
    setEditTarget(grant)
    setEditWaitDays(grant.waitDays)
    setEditAccessType(grant.accessType === 'takeover' ? 'takeover' : 'view')
  }

  const onSubmitEditGrant = async (): Promise<void> => {
    if (editTarget === null || editing) return
    setEditing(true)
    try {
      await editGrant(editTarget.id, editWaitDays, editAccessType)
      setEditTarget(null)
    } finally {
      setEditing(false)
    }
  }

  const openTakeover = (received: ReceivedListItemWire): void => {
    setTakeoverTarget(received)
    setTakeoverPw('')
    setTakeoverPw2('')
    setTakeoverPhase('idle')
    setTakeoverErrorKey(null)
  }

  const onSubmitTakeover = async (): Promise<void> => {
    if (takeoverTarget === null || takeoverPhase === 'submitting') return
    if (takeoverPw.length < MASTER_PASSWORD_MIN_LEN) {
      setTakeoverErrorKey('settings.emergency.takeover.error_short')

      return
    }
    if (takeoverPw !== takeoverPw2) {
      setTakeoverErrorKey('settings.emergency.takeover.error_mismatch')

      return
    }
    setTakeoverPhase('submitting')
    setTakeoverErrorKey(null)
    try {
      const result = await emergencyTakeoverFlow(takeoverTarget.id, takeoverPw)

      setTakeoverDoneEmail(result.granterEmail)
      setTakeoverPw('')
      setTakeoverPw2('')
      setTakeoverPhase('done')
      void refresh()
    } catch (err: unknown) {
      setTakeoverErrorKey(
        err instanceof EmergencyError
          ? TAKEOVER_ERROR_KEYS[err.code]
          : 'settings.emergency.error.generic',
      )
      setTakeoverPhase('idle')
    }
  }

  // 緊急アクセスを使うには自分の emergency keypair が必要 (grantee 側で invite 受領 +
  // placeholder invite の backfill にも必須)。mount 時に lazy 初期化する
  // (idempotent: 既に初期化済 / セッション内で試行済なら no-op)。
  useEffect(() => {
    void initKeypair()
  }, [initKeypair])

  const onSubmitInvite = async (): Promise<void> => {
    if (submitting || !inviteEmail) return
    setSubmitting(true)
    try {
      // Ensure my keypair exists before granter wraps (cheap idempotent init).
      await initKeypair()
      await invite(inviteEmail, inviteWaitDays, inviteAccessType)
      setInviteEmail('')
      setInviteWaitDays(DEFAULT_WAIT_DAYS)
      setInviteAccessType('view')
      setInviteOpen(false)
    } finally {
      setSubmitting(false)
    }
  }

  const onUnlock = (id: string): void => {
    window.location.hash = `emergency/vault/${id}`
  }

  if (state.kind === 'loading') {
    return <div className="settings-item-sub">{t('settings.emergency.loading')}</div>
  }

  const grants = state.kind === 'ready' ? state.grants : []
  const received = state.kind === 'ready' ? state.received : []
  const activeGrants = grants.filter(g => !isTerminal(g.status))
  const historyGrants = grants.filter(g => isTerminal(g.status))
  const activeReceived = received.filter(r => !isTerminal(r.status))
  const historyReceived = received.filter(r => isTerminal(r.status))

  return (
    <div>
      {lastErrorKey !== null && (
        <Notice tone="danger" ico="alert">
          {t(lastErrorKey)}
        </Notice>
      )}

      {/* ── Granter panel ──────────────────────────── */}
      <div className="card settings-emergency-card">
        <div className="card-row-flex settings-emergency-head">
          <div>
            <div className="settings-item-label">{t('settings.emergency.grantor_title')}</div>
            <div className="settings-item-sub">{t('settings.emergency.grantor_sub')}</div>
          </div>
          <div className="settings-emergency-head-actions">
            {historyGrants.length > 0 && (
              <button
                type="button"
                className={`btn btn-ghost btn-sm${showGrantHistory ? ' is-active' : ''}`}
                onClick={() => setShowGrantHistory(v => !v)}
                aria-pressed={showGrantHistory}
                data-testid="emergency-grant-history-toggle"
              >
                <Icon name="clock" aria-hidden="true" />
                {showGrantHistory
                  ? t('settings.emergency.history_hide')
                  : t('settings.emergency.history_show', { count: historyGrants.length })}
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setInviteOpen(v => !v)}
              data-testid="emergency-invite-toggle"
            >
              <Icon name="plus" aria-hidden="true" />
              {t('settings.emergency.grantor_add')}
            </button>
          </div>
        </div>

        {inviteOpen && (
          <div className="settings-emergency-invite-form" data-testid="emergency-invite-form">
            <div className="field">
              <label className="field-label" htmlFor="emergency-invite-email">
                {t('settings.emergency.invite.email_label')}
              </label>
              <input
                id="emergency-invite-email"
                className="input"
                type="email"
                value={inviteEmail}
                onChange={ev => setInviteEmail(ev.target.value)}
                required
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="emergency-invite-wait">
                {t('settings.emergency.invite.wait_days_label')}
              </label>
              <select
                id="emergency-invite-wait"
                className="select"
                value={inviteWaitDays}
                onChange={ev => setInviteWaitDays(Number(ev.target.value))}
              >
                {WAIT_OPTIONS.map(d => (
                  <option key={d} value={d}>
                    {formatWait(t, d)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="field-label" htmlFor="emergency-invite-access">
                {t('settings.emergency.invite.access_type_label')}
              </label>
              <select
                id="emergency-invite-access"
                className="select"
                value={inviteAccessType}
                onChange={ev => setInviteAccessType(ev.target.value as 'view' | 'takeover')}
              >
                <option value="view">{t('settings.emergency.access_type.view')}</option>
                <option value="takeover">{t('settings.emergency.access_type.takeover')}</option>
              </select>
              <div className="field-help">
                {t(`settings.emergency.invite.access_type_hint_${inviteAccessType}`)}
              </div>
            </div>
            <div className="settings-form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setInviteOpen(false)}
                disabled={submitting}
              >
                {t('settings.emergency.invite.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={submitting || !inviteEmail}
                onClick={() => void onSubmitInvite()}
                data-testid="emergency-invite-submit"
              >
                {submitting
                  ? t('settings.emergency.invite.submitting')
                  : t('settings.emergency.invite.submit')}
              </button>
            </div>
          </div>
        )}

        {activeGrants.length === 0 ? (
          <EmptyState
            icon="user"
            title={t('settings.emergency.grantor_empty_title')}
            sub={t('settings.emergency.grantor_empty_sub')}
          />
        ) : (
          activeGrants.map(g => (
            <GrantRow
              key={g.id}
              grant={g}
              onApprove={approve}
              onDeny={deny}
              onRevoke={setRevokeTarget}
              onEdit={openEditGrant}
              onRewrap={onRewrapGrant}
            />
          ))
        )}

        {showGrantHistory && historyGrants.length > 0 && (
          <div className="settings-emergency-history" data-testid="emergency-grant-history">
            <div className="settings-emergency-history-head">
              <Icon name="clock" aria-hidden="true" />
              {t('settings.emergency.history_title')}
            </div>
            {historyGrants.map(g => (
              <GrantRow
                key={g.id}
                grant={g}
                onApprove={approve}
                onDeny={deny}
                onRevoke={setRevokeTarget}
                onEdit={openEditGrant}
                onRewrap={onRewrapGrant}
                onDelete={deleteEntry}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Grantee panel ──────────────────────────── */}
      <div className="card settings-emergency-card">
        <div className="card-row-flex settings-emergency-head">
          <div className="settings-item-label settings-emergency-grantee-title">
            {t('settings.emergency.grantee_title')}
          </div>
          {historyReceived.length > 0 && (
            <button
              type="button"
              className={`btn btn-ghost btn-sm${showReceivedHistory ? ' is-active' : ''}`}
              onClick={() => setShowReceivedHistory(v => !v)}
              aria-pressed={showReceivedHistory}
              data-testid="emergency-received-history-toggle"
            >
              <Icon name="clock" aria-hidden="true" />
              {showReceivedHistory
                ? t('settings.emergency.history_hide')
                : t('settings.emergency.history_show', { count: historyReceived.length })}
            </button>
          )}
        </div>

        {activeReceived.length === 0 ? (
          <EmptyState
            icon="shield"
            title={t('settings.emergency.grantee_empty_title')}
            sub={t('settings.emergency.grantee_empty_sub')}
          />
        ) : (
          activeReceived.map(r => (
            <ReceivedRow
              key={r.id}
              received={r}
              onAccept={accept}
              onDecline={decline}
              onRequest={request}
              onCancelRequest={cancelRequest}
              onUnlock={onUnlock}
              onTakeover={openTakeover}
            />
          ))
        )}

        {showReceivedHistory && historyReceived.length > 0 && (
          <div className="settings-emergency-history" data-testid="emergency-received-history">
            <div className="settings-emergency-history-head">
              <Icon name="clock" aria-hidden="true" />
              {t('settings.emergency.history_title')}
            </div>
            {historyReceived.map(r => (
              <ReceivedRow
                key={r.id}
                received={r}
                onAccept={accept}
                onDecline={decline}
                onRequest={request}
                onCancelRequest={cancelRequest}
                onUnlock={onUnlock}
                onTakeover={openTakeover}
                onDelete={deleteEntry}
              />
            ))}
          </div>
        )}
      </div>

      {/* #4: 取消の確認ダイアログ。 */}
      {revokeTarget !== null && (
        <div
          className="vault-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="emergency-revoke-title"
        >
          <div className="vault-delete-dialog">
            <h3 id="emergency-revoke-title">{t('settings.emergency.revoke_confirm.title')}</h3>
            <p>
              {t('settings.emergency.revoke_confirm.body', {
                email: revokeTarget.granteeEmail,
              })}
            </p>
            <div className="vault-delete-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setRevokeTarget(null)}
                disabled={revoking}
              >
                {t('settings.emergency.revoke_confirm.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => void onConfirmRevoke()}
                disabled={revoking}
                data-testid="emergency-revoke-confirm"
              >
                <Icon name="trash" aria-hidden="true" />
                {t('settings.emergency.revoke_confirm.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* #3: テイクオーバー モーダル (granter のマスターパスワード再設定)。 */}
      {takeoverTarget !== null && (
        <div
          className="vault-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="emergency-takeover-title"
        >
          <div className="vault-delete-dialog">
            <h3 id="emergency-takeover-title">{t('settings.emergency.takeover.title')}</h3>
            {takeoverPhase === 'done' ? (
              <>
                <Notice tone="info" ico="check">
                  {t('settings.emergency.takeover.done', { email: takeoverDoneEmail })}
                </Notice>
                <div className="vault-delete-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setTakeoverTarget(null)}
                  >
                    {t('settings.emergency.takeover.close')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>
                  {t('settings.emergency.takeover.body', {
                    email: takeoverTarget.granterEmail,
                  })}
                </p>
                <Notice tone="warn" ico="alert">
                  {t('settings.emergency.takeover.warning')}
                </Notice>
                <div className="field">
                  <label className="field-label" htmlFor="emergency-takeover-pw">
                    {t('settings.emergency.takeover.password_label')}
                  </label>
                  <PasswordInput
                    id="emergency-takeover-pw"
                    value={takeoverPw}
                    onChange={ev => setTakeoverPw(ev.target.value)}
                    autoComplete="new-password"
                    disabled={takeoverPhase === 'submitting'}
                  />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="emergency-takeover-pw2">
                    {t('settings.emergency.takeover.password_confirm_label')}
                  </label>
                  <PasswordInput
                    id="emergency-takeover-pw2"
                    value={takeoverPw2}
                    onChange={ev => setTakeoverPw2(ev.target.value)}
                    autoComplete="new-password"
                    disabled={takeoverPhase === 'submitting'}
                  />
                </div>
                {takeoverErrorKey !== null && (
                  <Notice tone="danger" ico="alert">
                    {t(takeoverErrorKey)}
                  </Notice>
                )}
                <div className="vault-delete-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setTakeoverTarget(null)}
                    disabled={takeoverPhase === 'submitting'}
                  >
                    {t('settings.emergency.takeover.cancel')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => void onSubmitTakeover()}
                    disabled={takeoverPhase === 'submitting' || !takeoverPw || !takeoverPw2}
                    data-testid="emergency-takeover-submit"
                  >
                    {takeoverPhase === 'submitting'
                      ? t('settings.emergency.takeover.submitting')
                      : t('settings.emergency.takeover.submit')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 信頼者ルールの編集モーダル (待機日数 / アクセス種別)。 */}
      {editTarget !== null && (
        <div
          className="vault-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="emergency-edit-title"
        >
          <div className="vault-delete-dialog">
            <h3 id="emergency-edit-title">{t('settings.emergency.edit.title')}</h3>
            <p>{editTarget.granteeEmail}</p>
            <div className="field">
              <label className="field-label" htmlFor="emergency-edit-wait">
                {t('settings.emergency.invite.wait_days_label')}
              </label>
              <select
                id="emergency-edit-wait"
                className="select"
                value={editWaitDays}
                onChange={ev => setEditWaitDays(Number(ev.target.value))}
                disabled={editing}
              >
                {WAIT_OPTIONS.map(d => (
                  <option key={d} value={d}>
                    {formatWait(t, d)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="field-label" htmlFor="emergency-edit-access">
                {t('settings.emergency.invite.access_type_label')}
              </label>
              <select
                id="emergency-edit-access"
                className="select"
                value={editAccessType}
                onChange={ev => setEditAccessType(ev.target.value as 'view' | 'takeover')}
                disabled={editing}
              >
                <option value="view">{t('settings.emergency.access_type.view')}</option>
                <option value="takeover">{t('settings.emergency.access_type.takeover')}</option>
              </select>
              <div className="field-help">
                {t(`settings.emergency.invite.access_type_hint_${editAccessType}`)}
              </div>
            </div>
            <div className="vault-delete-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setEditTarget(null)}
                disabled={editing}
              >
                {t('settings.emergency.edit.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void onSubmitEditGrant()}
                disabled={editing}
                data-testid="emergency-edit-submit"
              >
                {editing ? t('settings.emergency.edit.saving') : t('settings.emergency.edit.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
